import {
  createContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  updateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, setDoc, getDoc, deleteDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { auth, db, googleProvider, facebookProvider } from '@/services/firebase';
import { updatePageUsername } from '@/services/pages';
import i18n from '@/i18n';

export interface AuthUser extends FirebaseUser {
  username?: string;
  role?: 'user' | 'admin';
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string, username: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithFacebook: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateDisplayName: (displayName: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  changeEmail: (currentPassword: string, newEmail: string) => Promise<void>;
  deleteAccount: (currentPassword: string) => Promise<void>;
  updateUsername: (newUsername: string) => Promise<void>;
  clearError: () => void;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Fetch additional user data from Firestore
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        const userData = userDoc.data();

        setUser({
          ...firebaseUser,
          username: userData?.username,
          role: userData?.role || 'user',
        } as AuthUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const createUserDocument = async (
    uid: string,
    email: string,
    displayName: string,
    username: string,
    photoURL?: string
  ) => {
    await setDoc(doc(db, 'users', uid), {
      uid,
      email,
      displayName,
      username,
      photoURL: photoURL || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };

  const checkUsernameAvailability = async (username: string): Promise<boolean> => {
    const usernameDoc = await getDoc(doc(db, 'usernames', username.toLowerCase()));
    return !usernameDoc.exists();
  };

  const reserveUsername = async (username: string, uid: string) => {
    await setDoc(doc(db, 'usernames', username.toLowerCase()), { uid });
  };

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('auth.failedToSignIn');
      setError(message);
      throw err;
    }
  };

  const register = async (
    email: string,
    password: string,
    displayName: string,
    username: string
  ) => {
    try {
      setError(null);

      // Check username availability
      const isAvailable = await checkUsernameAvailability(username);
      if (!isAvailable) {
        throw new Error(i18n.t('auth.usernameAlreadyTaken'));
      }

      // Create Firebase auth user
      const { user: firebaseUser } = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Update display name
      await updateProfile(firebaseUser, { displayName });

      // Reserve username and create user document
      await reserveUsername(username, firebaseUser.uid);
      await createUserDocument(
        firebaseUser.uid,
        email,
        displayName,
        username
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('auth.failedToCreateAccount');
      setError(message);
      throw err;
    }
  };

  const loginWithGoogle = async () => {
    try {
      setError(null);
      const { user: firebaseUser } = await signInWithPopup(auth, googleProvider);

      // Check if user document exists
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

      if (!userDoc.exists()) {
        // Waiting list mode: do not create accounts via Google login
        await signOut(auth);
        throw new Error(i18n.t('auth.noAccountFound'));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('auth.failedToSignInWithGoogle');
      setError(message);
      throw err;
    }
  };

  const loginWithFacebook = async () => {
    try {
      setError(null);
      const { user: firebaseUser } = await signInWithPopup(auth, facebookProvider);

      // Check if user document exists
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

      if (!userDoc.exists()) {
        // Waiting list mode: do not create accounts via Facebook login
        await signOut(auth);
        throw new Error(i18n.t('auth.noAccountFound'));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('auth.failedToSignInWithFacebook');
      setError(message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('auth.failedToSignOut');
      setError(message);
      throw err;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('auth.failedToSendResetEmail');
      setError(message);
      throw err;
    }
  };

  const reauthenticate = async (currentPassword: string) => {
    if (!auth.currentUser || !auth.currentUser.email) {
      throw new Error(i18n.t('auth.noAuthenticatedUser'));
    }
    const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, credential);
  };

  const updateDisplayName = async (displayName: string) => {
    try {
      setError(null);
      if (!auth.currentUser) throw new Error(i18n.t('auth.noAuthenticatedUser'));
      await updateProfile(auth.currentUser, { displayName });
      await setDoc(doc(db, 'users', auth.currentUser.uid), { displayName, updatedAt: serverTimestamp() }, { merge: true });
      setUser((prev) => prev ? { ...prev, displayName } as AuthUser : null);
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('auth.failedToUpdateDisplayName');
      setError(message);
      throw err;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      setError(null);
      if (!auth.currentUser) throw new Error(i18n.t('auth.noAuthenticatedUser'));
      await reauthenticate(currentPassword);
      await updatePassword(auth.currentUser, newPassword);
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('auth.failedToChangePassword');
      setError(message);
      throw err;
    }
  };

  const changeEmail = async (currentPassword: string, newEmail: string) => {
    try {
      setError(null);
      if (!auth.currentUser) throw new Error(i18n.t('auth.noAuthenticatedUser'));
      await reauthenticate(currentPassword);
      await updateEmail(auth.currentUser, newEmail);
      await setDoc(doc(db, 'users', auth.currentUser.uid), { email: newEmail, updatedAt: serverTimestamp() }, { merge: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('auth.failedToChangeEmail');
      setError(message);
      throw err;
    }
  };

  const deleteAccount = async (currentPassword: string) => {
    try {
      setError(null);
      if (!auth.currentUser) throw new Error(i18n.t('auth.noAuthenticatedUser'));
      const isOAuth = auth.currentUser.providerData.some(
        (p) => p.providerId === 'google.com' || p.providerId === 'facebook.com'
      );
      if (!isOAuth) {
        await reauthenticate(currentPassword);
      }
      const uid = auth.currentUser.uid;
      await deleteDoc(doc(db, 'users', uid));
      await deleteUser(auth.currentUser);
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('auth.failedToDeleteAccount');
      setError(message);
      throw err;
    }
  };

  const updateUsername = async (newUsername: string) => {
    try {
      setError(null);
      if (!auth.currentUser) throw new Error(i18n.t('auth.noAuthenticatedUser'));

      const normalizedUsername = newUsername.toLowerCase();

      // Validate format (letters, numbers, underscore, dot, 3+ chars)
      const usernameRegex = /^[a-zA-Z0-9_.]+$/;
      if (newUsername.length < 3) {
        throw new Error(i18n.t('auth.usernameTooShort'));
      }
      if (!usernameRegex.test(newUsername)) {
        throw new Error(i18n.t('auth.usernameInvalidChars'));
      }

      // Check availability
      const isAvailable = await checkUsernameAvailability(normalizedUsername);
      if (!isAvailable) {
        throw new Error(i18n.t('auth.usernameAlreadyTaken'));
      }

      const uid = auth.currentUser.uid;
      const oldUsername = user?.username?.toLowerCase();

      // Use batch write to update atomically
      const batch = writeBatch(db);

      // Delete old username reservation (if exists)
      if (oldUsername) {
        const oldUsernameRef = doc(db, 'usernames', oldUsername);
        batch.delete(oldUsernameRef);
      }

      // Create new username reservation
      const newUsernameRef = doc(db, 'usernames', normalizedUsername);
      batch.set(newUsernameRef, { uid });

      // Update user document
      const userRef = doc(db, 'users', uid);
      batch.update(userRef, {
        username: normalizedUsername,
        updatedAt: serverTimestamp()
      });

      // Commit batch
      await batch.commit();

      // Update page username
      await updatePageUsername(uid, normalizedUsername);

      // Update local state
      setUser((prev) => prev ? { ...prev, username: normalizedUsername } as AuthUser : null);
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('settings.updateFailed');
      setError(message);
      throw err;
    }
  };

  const clearError = () => setError(null);

  const value: AuthContextValue = {
    user,
    loading,
    error,
    login,
    register,
    loginWithGoogle,
    loginWithFacebook,
    logout,
    resetPassword,
    updateDisplayName,
    changePassword,
    changeEmail,
    deleteAccount,
    updateUsername,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
